import { useEffect, useState } from 'react';
import { Chat, Message } from '../types/chat';
import { useSocketContext } from '../context/SocketContext';
import { useAuth } from '../context/AuthContext';
import { chatAPI, systemChatAPI, groupChatAPI } from '../services/api';

export const useChatMessages = (chat: Chat | null, isGroupChat: boolean = false) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const socket = useSocketContext();
  const { user } = useAuth();

  useEffect(() => {
    const loadChatMessages = async () => {
      if (!chat) {
        setMessages([]);
        return;
      }

      try {
        console.log(
          'useChatMessages: Загружаем сообщения для',
          isGroupChat ? 'группового' : 'обычного/системного',
          'чата',
          chat.id,
        );

        if (chat.id === 0) {
          const response = await systemChatAPI.getSystemChat();
          setMessages(response.data.messages || []);
        } else if (isGroupChat) {
          const response = await groupChatAPI.getGroupWithMessages(chat.id);
          setMessages(response.data.messages || []);
        } else {
          const response = await chatAPI.getChatWithMessages(chat.id);
          setMessages(response.data.messages || []);
        }
      } catch (error) {
        console.error('Ошибка загрузки сообщений:', error);
        setMessages(chat.messages || []);
      }
    };

    loadChatMessages();
  }, [chat, isGroupChat]);

  useEffect(() => {
    if (!socket || !user || !chat) return;

    const handleNewMessage = (message: Message) => {
      console.log('🎯 useChatMessages: Получено новое сообщение:', message);

      if (isGroupChat && message.groupId !== chat.id) {
        console.log('⚠️ Сообщение не для текущего группового чата, пропускаем');
        return;
      } else if (!isGroupChat && message.chatId !== chat.id) {
        console.log('⚠️ Сообщение не для текущего чата, пропускаем');
        return;
      }

      const currentUserNumber = user?.userNumber;
      const isOwnMessage = message.senderNumber === currentUserNumber;
      const messageWithOwnership = { ...message, isOwn: isOwnMessage };

      setMessages((prevMessages) => {
        console.log('📝 Предыдущие сообщения:', prevMessages.length);

        if (message.tempId) {
          const existsByTempId = prevMessages.some((msg) => msg.tempId === message.tempId);
          if (existsByTempId) {
            console.log('✅ Обновляем сообщение по tempId:', message.tempId);
            return prevMessages.map((msg) => (msg.tempId === message.tempId ? messageWithOwnership : msg));
          }
        }

        const existsById = prevMessages.some((msg) => msg.id === message.id);
        if (existsById) {
          console.log('⚠️ Сообщение уже существует, пропускаем');
          return prevMessages;
        }

        console.log('✅ Добавляем новое сообщение');
        return [...prevMessages, messageWithOwnership];
      });
    };

    const handleNewSystemMessage = (message: Message) => {
      console.log('🎯 useChatMessages: Получено системное сообщение:', message);

      if (chat.id !== 0) {
        console.log('⚠️ Системное сообщение не для системного чата, пропускаем');
        return;
      }

      const currentUserNumber = user?.userNumber;
      const isOwnMessage = message.senderNumber === currentUserNumber;
      const messageWithOwnership = { ...message, isOwn: isOwnMessage };

      setMessages((prevMessages) => {
        if (message.tempId) {
          const existsByTempId = prevMessages.some((msg) => msg.tempId === message.tempId);
          if (existsByTempId) {
            return prevMessages.map((msg) => (msg.tempId === message.tempId ? messageWithOwnership : msg));
          }
        }

        const existsById = prevMessages.some((msg) => msg.id === message.id);
        if (!existsById) {
          return [...prevMessages, messageWithOwnership];
        }
        return prevMessages;
      });
    };

    const handleChatCleared = (data: {
      chatId: number;
      clearedBy: number;
      clearedByUserNumber: string;
      timestamp: string;
    }) => {
      console.log('🧹 useChatMessages: Получено событие очистки чата:', data);

      if (data.chatId === chat.id && !isGroupChat) {
        console.log('✅ Удаляем сообщения пользователя', data.clearedBy);
        setMessages((prevMessages) => prevMessages.filter((message) => message.senderId !== data.clearedBy));
      }
    };

    const handleNewGroupMessage = (message: Message) => {
      console.log('🎯 useChatMessages: Получено групповое сообщение:', message);

      if (isGroupChat && message.groupId === chat.id) {
        const currentUserNumber = user?.userNumber;
        const isOwnMessage = message.senderNumber === currentUserNumber;
        const messageWithOwnership = { ...message, isOwn: isOwnMessage };

        setMessages((prevMessages) => {
          if (message.tempId) {
            const existsByTempId = prevMessages.some((msg) => msg.tempId === message.tempId);
            if (existsByTempId) {
              return prevMessages.map((msg) => (msg.tempId === message.tempId ? messageWithOwnership : msg));
            }
          }

          const existsById = prevMessages.some((msg) => msg.id === message.id);
          if (!existsById) {
            return [...prevMessages, messageWithOwnership];
          }
          return prevMessages;
        });
      }
    };

    const removeNewMessage = socket.on(isGroupChat ? 'new_group_message' : 'new_message', handleNewMessage);
    const removeNewSystemMessage = socket.on('new_system_message', handleNewSystemMessage);
    const removeChatCleared = socket.on('chat_cleared', handleChatCleared);
    const removeNewGroupMessage = isGroupChat ? socket.on('new_group_message', handleNewGroupMessage) : () => {};

    return () => {
      console.log('useChatMessages: Отписываемся от событий сообщений');
      removeNewMessage();
      removeNewSystemMessage();
      removeChatCleared();
      removeNewGroupMessage();
    };
  }, [socket, user, chat, isGroupChat]);

  const addTempMessage = (content: string, tempId: string) => {
    if (!user || !chat) return;

    const tempMessage: Message = {
      id: 0,
      tempId,
      content,
      senderNumber: user.userNumber,
      senderId: user.id,
      chatId: isGroupChat ? undefined : chat.id,
      groupId: isGroupChat ? chat.id : undefined,
      timestamp: new Date().toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }),
      createdAt: new Date().toISOString(),
      isOwn: true,
    };

    console.log('➕ Добавляем временное сообщение:', tempMessage);
    setMessages((prev) => [...prev, tempMessage]);
  };

  return {
    messages,
    addTempMessage,
    setMessages,
  };
};
